
import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Button, Space, Input, Select } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { AppDispatch } from "../../../../core/data/redux/store";
import { 
  fetchReferralRewards, 
  updateReferralRewardStatus, 
  fetchReferralRewardSummary 
} from "../../../../core/data/redux/recruitment/thunks";
import { notify } from "../../../../services/toast.config";
import ApplicationPagination from "../../applications/components/ApplicationPagination";
import { humanReadableDate } from "../../../../helper/dateTimeHelper";

const { Option } = Select;

const ReferralRewardsDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { 
    referralRewards, 
    referralRewardsLoading, 
    referralRewardSummary,
    activeCompany 
  } = useSelector((state: any) => state.recruitment);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    status: "all",
    stage: "all",
    search: "",
  });

  useEffect(() => {
    if (activeCompany?.company_id) {
      dispatch(fetchReferralRewards({ ...filters, page, limit }));
      dispatch(fetchReferralRewardSummary());
    }
  }, [dispatch, activeCompany?.company_id, filters, page, limit]);

  const handleStatusUpdate = useCallback(async (rewardId: number, status: string) => {
    try {
      await dispatch(updateReferralRewardStatus({ rewardId, status })).unwrap();
      notify.success(`Reward marked as ${status}`);
      dispatch(fetchReferralRewards({ ...filters, page, limit }));
      dispatch(fetchReferralRewardSummary());
    } catch (err: any) {
      notify.error(err || "Failed to update status");
    }
  }, [dispatch, filters, page, limit]);

  const columns: ColumnsType<any> = [
    {
      title: "Candidate",
      key: "candidate",
      render: (_, row) => (
        <div>
          <div className="fw-medium fs-14">{row.candidate.name}</div>
          <span className="text-muted fs-11">{row.candidate.email}</span>
        </div>
      ),
    },
    {
      title: "Job",
      key: "job",
      render: (_, row) => (
        <div>
          <div className="fs-13 text-dark">{row.job.title}</div>
          <span className="badge badge-soft-secondary fs-10" style={{ fontSize: 9 }}>{row.job.code}</span>
        </div>
      ),
    },
    {
      title: "Milestone",
      dataIndex: "stage",
      key: "stage",
      render: (stage: string) => (
        <span className="text-capitalize fs-13">
          {stage.replace(/_/g, " ")}
        </span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => (
        <span className="fw-bold text-success fs-14">
          ${Number(amount).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Referrer",
      key: "referrer",
      render: (_, row) => row.referrer ? (
        <div>
          <div className="fs-13">{row.referrer.name}</div>
          <div className="text-muted fs-11">{row.referrer.email}</div>
        </div>
      ) : <span className="text-muted">—</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const colors: any = {
          pending: "orange",
          approved: "blue",
          paid: "success",
          rejected: "red",
        };
        return (
          <Tag color={colors[status] || "default"} className="rounded-pill px-2" style={{ textTransform: 'capitalize' }}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      align: "center",
      render: (_, row) => (
        <Space size="middle">
          {row.status === "pending" && (
            <Button 
              type="primary" 
              size="small" 
              className="bg-info border-info"
              onClick={() => handleStatusUpdate(row.id, "approved")}
            >
              Approve
            </Button>
          )}
          {row.status === "approved" && (
            <Button 
              type="primary" 
              size="small" 
              className="bg-success border-success"
              onClick={() => handleStatusUpdate(row.id, "paid")}
            >
              Mark Paid
            </Button>
          )}
          {["pending", "approved"].includes(row.status) && (
            <Button 
              type="link" 
              danger 
              size="small" 
              onClick={() => handleStatusUpdate(row.id, "rejected")}
            >
              Reject
            </Button>
          )}
          {row.status === "paid" && (
             <span className="text-muted fs-11">
               <i className="ti ti-calendar-check me-1"></i>
               {humanReadableDate(row.paid_at)}
             </span>
          )}
        </Space>
      ),
    },
  ];

  const getSummary = (status: string) => {
    const item = referralRewardSummary.find((s: any) => s.status === status);
    return {
      count: item ? parseInt(item.count) : 0,
      amount: item ? parseFloat(item.total_amount) : 0
    };
  };

  return (
    <div className="referral-rewards-container">
      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        {[
          { label: "Pending Approval", status: "pending", color: "warning", icon: "ti-clock" },
          { label: "Approved (To Pay)", status: "approved", color: "info", icon: "ti-check" },
          { label: "Total Paid", status: "paid", color: "success", icon: "ti-wallet" },
        ].map((card) => {
          const data = getSummary(card.status);
          return (
            <div className="col-md-4" key={card.label}>
              <div className="card border-0 shadow-sm overflow-hidden h-100">
                <div className="card-body p-3">
                  <div className="d-flex align-items-center mb-2">
                    <div className={`avatar avatar-md bg-${card.color}-transparent rounded-circle me-3 d-flex align-items-center justify-content-center`}>
                      <i className={`ti ${card.icon} fs-20 text-${card.color}`}></i>
                    </div>
                    <div>
                      <div className="text-muted fs-12 fw-medium">{card.label}</div>
                      <h4 className="mb-0 fw-bold">${data.amount.toLocaleString()}</h4>
                    </div>
                  </div>
                  <div className="fs-11 text-muted">
                    <span className="fw-medium text-dark">{data.count}</span> records processed
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters & Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <h5 className="mb-0 fs-16 fw-bold">Reward Tracking Details</h5>
          <div className="d-flex gap-2">
            <Select 
              value={filters.status}
              onChange={(val) => setFilters({ ...filters, status: val })}
              style={{ width: 140 }}
              size="small"
            >
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="paid">Paid</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
            <Input 
              placeholder="Search candidate..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              style={{ width: 200 }}
              size="small"
              prefix={<i className="ti ti-search text-muted fs-12"></i>}
            />
          </div>
        </div>
        <div className="card-body p-0">
          <Table 
            columns={columns} 
            dataSource={referralRewards} 
            loading={referralRewardsLoading}
            pagination={false}
            rowKey="id"
            className="custom-table"
            scroll={{ x: 900 }}
          />
          <ApplicationPagination
            currentPage={page}
            totalPages={Math.ceil((referralRewards?.[0]?.total_count || 0) / limit) || 1}
            total={referralRewards?.[0]?.total_count || referralRewards.length}
            limit={limit}
            hasNextPage={referralRewards.length === limit}
            hasPrevPage={page > 1}
            onPageChange={(p) => setPage(p)}
            onLimitChange={(l) => { setLimit(l); setPage(1); }}
          />
        </div>
      </div>
    </div>
  );
};

export default ReferralRewardsDashboard;
